import { accountsABI } from '@celo/abis';
import { App, Octokit } from 'octokit';
import path from 'path';
import { Addresses } from 'src/config/contracts';
import { links } from 'src/config/links';
import {
  delegateeRegistrationRequestToMetadata,
  metadataToJSONString,
} from 'src/features/delegation/delegateeMetadata';
import { DelegateeMetadata, RegisterDelegateRequest } from 'src/features/delegation/types';
import { celoPublicClient } from 'src/utils/client';

export async function isAddressAnAccount(address: HexString) {
  return await celoPublicClient.readContract({
    address: Addresses.Accounts,
    abi: accountsABI,
    functionName: 'isAccount',
    args: [address],
  });
}

const fetchFileShaIfExists = async (
  octokit: Octokit,
  repoOwner: string,
  repoName: string,
  branchRef: string,
  filePath: string,
): Promise<string | null> => {
  try {
    const { data } = await octokit.rest.repos.getContent({
      owner: repoOwner,
      repo: repoName,
      path: filePath,
      ref: branchRef,
    });

    // NOTE: typing changed and we need to assert this is indeed a file as
    // octokit doesnt seem to export the OctokitResponse<{type: 'file'}>
    if (!Array.isArray(data)) {
      if (data.type === 'file') {
        return data.sha;
      }
    }
    throw new Error(`Don't know what to do with file/dir fetched at ${filePath}`);
  } catch (err) {
    const notFound = err instanceof Error && 'status' in err && err.status === 404;

    if (!notFound) {
      throw err;
    }
  }

  return null;
};

export async function createDelegationPR(request: RegisterDelegateRequest) {
  const GITHUB_APP_ID = process.env['GITHUB_APP_ID'] as string;
  const GITHUB_PRIVATE_KEY = process.env['GITHUB_PRIVATE_KEY'] as string;
  const GITHUB_INSTALLATION_ID = parseInt(process.env['GITHUB_INSTALLATION_ID'] as string);

  const app = new App({
    appId: GITHUB_APP_ID,
    privateKey: GITHUB_PRIVATE_KEY,
  });

  const GITHUB_REPO_OWNER = process.env['GITHUB_REPO_OWNER'] as string;
  const GITHUB_REPO_NAME = process.env['GITHUB_REPO_NAME'] as string;
  const octokit = await app.getInstallationOctokit(GITHUB_INSTALLATION_ID);

  const mainBranch = await octokit.rest.repos.getBranch({
    owner: GITHUB_REPO_OWNER,
    repo: GITHUB_REPO_NAME,
    branch: 'main',
  });

  const branchName = `delegatee/${request.address}-${Date.now()}`;
  const metadataPath = `src/config/delegatees/${request.address}.json`;
  const imagePath = `public/logos/delegatees/${request.address}${path.extname(request.image!.name)}`;
  const delegateeMetadata = delegateeRegistrationRequestToMetadata(request, new Date());

  const newRefResponse = await octokit.rest.git.createRef({
    owner: GITHUB_REPO_OWNER,
    repo: GITHUB_REPO_NAME,
    ref: `refs/heads/${branchName}`,
    sha: mainBranch.data.commit.sha,
  });

  const [metadataFileSha, imageFileSha] = await Promise.all([
    fetchFileShaIfExists(
      octokit,
      GITHUB_REPO_OWNER,
      GITHUB_REPO_NAME,
      newRefResponse.data.ref,
      metadataPath,
    ),
    fetchFileShaIfExists(
      octokit,
      GITHUB_REPO_OWNER,
      GITHUB_REPO_NAME,
      newRefResponse.data.ref,
      imagePath,
    ),
  ]);

  await Promise.all([
    octokit.rest.repos.createOrUpdateFileContents({
      owner: GITHUB_REPO_OWNER,
      repo: GITHUB_REPO_NAME,
      path: metadataPath,
      message: `Adding delegatee ${request.address} metadata`,
      content: Buffer.from(metadataToJSONString(delegateeMetadata)).toString('base64'),
      sha: metadataFileSha || undefined,
      branch: branchName,
    }),
    octokit.rest.repos.createOrUpdateFileContents({
      owner: GITHUB_REPO_OWNER,
      repo: GITHUB_REPO_NAME,
      path: imagePath,
      message: `Adding delegatee ${request.address} image`,
      content: Buffer.from(await request.image!.arrayBuffer()).toString('base64'),
      sha: imageFileSha || undefined,
      branch: branchName,
    }),
  ]);

  const createPRResponse = await octokit.rest.pulls.create({
    owner: GITHUB_REPO_OWNER,
    repo: GITHUB_REPO_NAME,
    head: branchName,
    base: 'main',
    title: `Adding delegate ${request.name}`,
    body: `Adding delegate \`${request.name}\` with address [${request.address}](${links.blockscout}/address/${request.address}).
      
[Verification](${request.verificationUrl}) (\`${request.verificationUrl}\`)`,
  });

  return createPRResponse.data.html_url;
}
export function getXName(data: DelegateeMetadata) {
  const link = data.links.twitter;

  if (link?.startsWith('https://')) {
    return `@${link.split('.com/')[1]}`;
  }
  return undefined;
}
